// @ts-check

import { onDomReady } from '../utils/dom';

/**
 * Eigene Player-Steuerung für jedes <video controls> (Player-CE, fslightbox-Video).
 *
 * Native Video-Controls lassen sich nur in Chromium stylen (::-webkit-media-controls-*), Firefox und Safari zeigen immer
 * ihre eigene Optik. Diese Komponente entfernt das controls-Attribut und hängt eine Leiste in Theme-Farben hinter das
 * Video (Optik: components/_video-controls.scss). Ohne JavaScript bleibt das controls-Attribut stehen → native Steuerung.
 *
 * Bedienung: Play/Pause (Schaltfläche, Klick aufs Video, Taste K), Zeitleiste (Range, Pfeiltasten ±5 s), Zeitanzeige,
 * Ton an/aus (Taste M), Vollbild (Taste F; iOS ohne Element-Fullscreen → nativer Vollbildplayer). Die Leiste blendet
 * sich während der Wiedergabe nach IDLE_TIMEOUT aus und bei Mausbewegung, Fokus oder Pause wieder ein.
 *
 * Videos, die erst später ins DOM kommen (fslightbox erzeugt sein <video> beim Öffnen), findet ein MutationObserver.
 * Der Host (Elternelement des Videos) bekommt die Klasse .video-controls-host und wird Bezug für die absolute Leiste.
 */

const IDLE_TIMEOUT = 2500;
const SEEK_STEP = 5;
const NARROW_WIDTH = 480; // darunter entfällt die Gesamtdauer in der Zeitanzeige

const LABELS = {
    de: {
        play: 'Abspielen',
        pause: 'Pausieren',
        mute: 'Ton aus',
        unmute: 'Ton an',
        fullscreen: 'Vollbild',
        exitFullscreen: 'Vollbild beenden',
        timeline: 'Wiedergabeposition',
        of: 'von',
    },
    en: {
        play: 'Play',
        pause: 'Pause',
        mute: 'Mute',
        unmute: 'Unmute',
        fullscreen: 'Full screen',
        exitFullscreen: 'Exit full screen',
        timeline: 'Playback position',
        of: 'of',
    },
};

// 24er-Raster, currentColor; Strich 2 px mit eckigen Enden wie die Social-Icons des Themes
const ICONS = {
    play: '<path d="M7 4l13 8-13 8z"/>',
    pause: '<path d="M6 4h4v16H6zM14 4h4v16h-4z"/>',
    volume:
        '<path d="M3 9h4l5-4v14l-5-4H3z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" fill="none" stroke="currentColor" stroke-width="2"/>',
    muted: '<path d="M3 9h4l5-4v14l-5-4H3z"/><path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" stroke-width="2"/>',
    expand: '<path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" fill="none" stroke="currentColor" stroke-width="2"/>',
    compress: '<path d="M9 4v5H4M15 4v5h5M20 15h-5v5M4 15h5v5" fill="none" stroke="currentColor" stroke-width="2"/>',
};

/**
 * @param {keyof typeof ICONS} name
 */
function icon(name) {
    return `<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true" focusable="false">${ICONS[name]}</svg>`;
}

/**
 * @param {number} seconds
 */
function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }

    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = h ? String(m).padStart(2, '0') : String(m);

    return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * @param {string} className
 * @param {string} label
 * @param {string} svg
 */
function createButton(className, label, svg) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `video-controls__button ${className}`;
    button.setAttribute('aria-label', label);
    button.innerHTML = svg;

    return button;
}

/**
 * @param {HTMLVideoElement} video
 */
function initVideoControls(video) {
    if (video.dataset.videoControls || !video.parentElement) {
        return;
    }

    video.dataset.videoControls = 'enhanced';

    const lang = document.documentElement.lang.slice(0, 2);
    const t = LABELS[lang] ?? LABELS.de;
    const host = video.parentElement;
    host.classList.add('video-controls-host');

    // Ab hier steuert die Leiste; das Attribut bleibt bis hierher stehen (No-JS-Fallback, Chromium-Styles in _video.scss)
    video.controls = false;
    video.removeAttribute('controls');

    const bar = document.createElement('div');
    bar.className = 'video-controls';

    const playButton = createButton('video-controls__play', t.play, icon('play'));

    const time = document.createElement('span');
    time.className = 'video-controls__time';
    const current = document.createElement('span');
    current.className = 'video-controls__current';
    current.textContent = '0:00';
    const separator = document.createElement('span');
    separator.className = 'video-controls__separator';
    separator.textContent = ' / ';
    const duration = document.createElement('span');
    duration.className = 'video-controls__duration';
    duration.textContent = '0:00';
    time.append(current, separator, duration);

    const timeline = document.createElement('input');
    timeline.type = 'range';
    timeline.className = 'video-controls__timeline';
    timeline.min = '0';
    timeline.max = '0';
    timeline.step = '0.1';
    timeline.value = '0';
    timeline.disabled = true;
    timeline.setAttribute('aria-label', t.timeline);

    const muteButton = createButton('video-controls__mute', t.mute, icon('volume'));
    const fullscreenButton = createButton('video-controls__fullscreen', t.fullscreen, icon('expand'));

    bar.append(playButton, time, timeline, muteButton, fullscreenButton);
    host.appendChild(bar);

    // Klicks auf die Leiste gehören dem Player, nicht dem Umfeld (fslightbox wertet pointerdown/-up für Wischen und
    // Schließen aus, Onepage-Handler lauschen auf click)
    ['pointerdown', 'pointerup', 'click'].forEach((type) => bar.addEventListener(type, (e) => e.stopPropagation()));

    let scrubbing = false;
    let idleTimer = 0;

    const setPlayState = () => {
        const paused = video.paused || video.ended;
        bar.classList.toggle('video-controls--paused', paused);
        playButton.innerHTML = icon(paused ? 'play' : 'pause');
        playButton.setAttribute('aria-label', paused ? t.play : t.pause);
    };

    const setMuteState = () => {
        const muted = video.muted || video.volume === 0;
        muteButton.innerHTML = icon(muted ? 'muted' : 'volume');
        muteButton.setAttribute('aria-label', muted ? t.unmute : t.mute);
        muteButton.setAttribute('aria-pressed', String(muted));
    };

    const setDuration = () => {
        const d = video.duration;
        const known = Number.isFinite(d) && d > 0;
        duration.textContent = known ? formatTime(d) : '0:00';
        timeline.max = known ? String(d) : '0';
        timeline.disabled = !known;
    };

    const setBuffered = () => {
        const d = video.duration;

        if (!Number.isFinite(d) || d <= 0) {
            return;
        }

        let end = 0;

        for (let i = 0; i < video.buffered.length; i++) {
            if (video.buffered.start(i) <= video.currentTime && video.buffered.end(i) > end) {
                end = video.buffered.end(i);
            }
        }

        timeline.style.setProperty('--buffered', `${Math.min(100, (end / d) * 100)}%`);
    };

    const setProgress = () => {
        const d = video.duration;
        const position = video.currentTime;
        current.textContent = formatTime(position);

        if (!Number.isFinite(d) || d <= 0) {
            return;
        }

        timeline.style.setProperty('--progress', `${Math.min(100, (position / d) * 100)}%`);
        timeline.setAttribute('aria-valuetext', `${formatTime(position)} ${t.of} ${formatTime(d)}`);

        if (!scrubbing) {
            timeline.value = String(position);
        }
    };

    const wake = () => {
        bar.classList.remove('video-controls--idle');
        host.classList.remove('video-controls-host--idle');
        clearTimeout(idleTimer);

        if (video.paused || video.ended) {
            return;
        }

        idleTimer = window.setTimeout(() => {
            if (bar.matches(':focus-within') || scrubbing) {
                wake();
                return;
            }

            bar.classList.add('video-controls--idle');
            host.classList.add('video-controls-host--idle');
        }, IDLE_TIMEOUT);
    };

    const togglePlay = () => {
        if (video.paused || video.ended) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    };

    const toggleMute = () => {
        video.muted = !video.muted;

        if (!video.muted && video.volume === 0) {
            video.volume = 1;
        }
    };

    const isFullscreen = () => document.fullscreenElement === host;

    const toggleFullscreen = () => {
        if (isFullscreen()) {
            document.exitFullscreen();
        } else if (host.requestFullscreen) {
            host.requestFullscreen().catch(() => {});
        } else if (/** @type {any} */ (video).webkitEnterFullscreen) {
            /** @type {any} */ (video).webkitEnterFullscreen(); // iOS Safari: nur das Video selbst, nativer Player
        }
    };

    const setFullscreenState = () => {
        const on = isFullscreen();
        host.classList.toggle('video-controls-host--fullscreen', on);
        fullscreenButton.innerHTML = icon(on ? 'compress' : 'expand');
        fullscreenButton.setAttribute('aria-label', on ? t.exitFullscreen : t.fullscreen);
    };

    if (!document.fullscreenEnabled && !(/** @type {any} */ (video).webkitSupportsFullscreen)) {
        fullscreenButton.hidden = true;
    }

    playButton.addEventListener('click', togglePlay);
    muteButton.addEventListener('click', toggleMute);
    fullscreenButton.addEventListener('click', toggleFullscreen);
    video.addEventListener('click', togglePlay);

    timeline.addEventListener('pointerdown', () => (scrubbing = true));
    timeline.addEventListener('input', () => {
        video.currentTime = Number(timeline.value);
        setProgress();
    });
    timeline.addEventListener('change', () => (scrubbing = false));
    timeline.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const direction = e.key === 'ArrowRight' ? 1 : -1;
            video.currentTime = Math.min(video.duration || 0, Math.max(0, video.currentTime + direction * SEEK_STEP));
        }
    });

    host.addEventListener('keydown', (e) => {
        if (e.altKey || e.ctrlKey || e.metaKey) {
            return;
        }

        const key = e.key.toLowerCase();

        if (key === 'k' || (key === ' ' && e.target === video)) {
            e.preventDefault();
            togglePlay();
        } else if (key === 'm') {
            e.preventDefault();
            toggleMute();
        } else if (key === 'f') {
            e.preventDefault();
            toggleFullscreen();
        }
    });

    video.addEventListener('play', () => {
        setPlayState();
        wake();
    });
    video.addEventListener('pause', () => {
        setPlayState();
        wake();
    });
    video.addEventListener('ended', setPlayState);
    video.addEventListener('volumechange', setMuteState);
    video.addEventListener('loadedmetadata', setDuration);
    video.addEventListener('durationchange', setDuration);
    video.addEventListener('timeupdate', setProgress);
    video.addEventListener('progress', setBuffered);
    video.addEventListener('seeking', setProgress);

    host.addEventListener('pointermove', wake);
    host.addEventListener('pointerdown', wake);
    bar.addEventListener('focusin', wake);
    document.addEventListener('fullscreenchange', setFullscreenState);

    // Escape im Vollbild beendet nur das Vollbild (Browser-Standard) — Umgebungs-Handler wie das Schließen der fslightbox
    // dürfen die Taste dann nicht auch noch auswerten
    document.addEventListener(
        'keydown',
        (e) => {
            if (e.key === 'Escape' && isFullscreen()) {
                e.stopPropagation();
            }
        },
        true
    );

    if ('ResizeObserver' in window) {
        new ResizeObserver(() => bar.classList.toggle('video-controls--narrow', video.clientWidth < NARROW_WIDTH)).observe(
            video
        );
    }

    setPlayState();
    setMuteState();
    setDuration();
    setProgress();
    setBuffered();
    wake();
}

/**
 * @param {ParentNode} root
 */
function initAll(root) {
    root.querySelectorAll('video[controls]').forEach((video) => initVideoControls(/** @type {HTMLVideoElement} */ (video)));
}

onDomReady(() => {
    initAll(document);

    // Später eingefügte Videos (fslightbox baut sein <video controls> erst beim Öffnen)
    new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (!(node instanceof Element)) {
                    continue;
                }

                if (node instanceof HTMLVideoElement) {
                    if (node.hasAttribute('controls')) {
                        initVideoControls(node);
                    }
                } else {
                    initAll(node);
                }
            }
        }
    }).observe(document.documentElement, { childList: true, subtree: true });
});
